// CMSP Chat Widget — UI string table (English defaults).
//
// Every piece of widget-owned UI text lives here rather than inline in the
// components, so a host can localize the widget WITHOUT a widget release.
// Deliberately English-only: shipping additional locales inside the bundle would
// grow every partner's download and force a CDN republish (+ new SRI hashes) per
// language. Hosts layer their own translations on via CmspChatConfig.strings.
//
// Values are plain strings (JSON-serializable) with `{token}` placeholders, so a
// partner can load a translation straight from their own resource files.

import { createContext, useContext } from 'react';
import type { CmspChatStreamChunk, CmspChatStrings, CmspChatStringsOverride } from './contract';

export const EN_STRINGS: CmspChatStrings = {
  // Server text is the default; never replace a backend-localized message with widget English.
  serverMessages: {},
  caseDetails: {
    toggle: 'Show or hide details for case {caseNumber}',
    heading: 'Case {caseNumber}',
    title: 'Case title',
    summary: 'Case summary',
    close: 'Close',
  },
  chrome: {
    defaultTitle: 'Assistant',
    defaultLauncherText: 'Chat with AI',
    launcherPrompt: 'Quick help?',
    liveTitle: 'Live agent',
    liveSubtitle: 'Human support conversation',
    connectingTitle: 'Connecting to support',
    connectingSubtitle: 'Starting a support conversation',
    waitingTitle: 'Waiting for a support agent',
    waitingSubtitle: 'You can send a message while you wait',
    failedTitle: 'Could not connect to support',
    failedSubtitle: 'End this chat or start a new chat to try again',
    newChatLabel: 'Start a new chat',
    newChatTitle: 'New chat',
    minimizeLabel: 'Minimize chat',
    minimizeTitle: 'Minimize',
    closeLabel: 'Close',
    expandChatLabel: 'Expand chat',
    expandLabel: 'Expand',
    minimizedToolbarLabel: '{title} (minimized)',
  },
  conversation: {
    welcomeTitle: "Hi, I'm your {title}.",
    welcomeFallbackTitle: 'assistant',
    welcomeBody: 'How can I help?',
    assistantLabel: 'Assistant',
    botLabel: 'Bot',
    seenBySupportLabel: 'Seen by support',
    liveAgentLabel: 'Live agent',
    assistantAvatar: 'AI',
    liveAgentAvatar: 'L',
    typingLabel: 'Assistant is typing',
    transcriptLabel: 'Conversation closed',
  },
  citations: {
    intro: 'Learn more about where this information comes from in',
    toggle: 'Sources',
    showTitle: 'Show sources',
    hideTitle: 'Hide sources',
    openReference: 'Open source reference {index}',
    reference: 'Source reference {index}',
  },
  handoff: {
    title: 'Contact Support',
    body: 'Upcoming feature transferring to Human agent',
  },
  composer: {
    placeholder: 'Ask anything…',
    livePlaceholder: 'Message live agent…',
    connectingPlaceholder: 'Connecting to support…',
    waitingPlaceholder: 'Message live support while you wait…',
    failedPlaceholder: 'Start a new chat to try again',
    inputLabel: 'Message',
    hint: 'Enter to send · Shift+Enter for a new line',
    stop: 'Stop',
    send: 'Send',
    disclaimer: 'AI-generated content may be incorrect',
  },
  feedback: {
    groupLabel: 'Was this helpful?',
    copy: 'Copy',
    copied: 'Copied',
    helpful: 'Helpful',
    notHelpful: 'Not helpful',
  },
  endChat: {
    title: 'End Chat',
    body: 'Do you want to close this chat and start fresh?',
    confirm: 'End Chat',
    cancel: 'Cancel',
    promptTitle: "We'd love your feedback",
    promptBody: 'Answer a few questions to help us improve the Support Assistant.',
    promptButton: 'Give feedback',
    thanksTitle: 'Thanks for your feedback!',
    thanksBody: 'Your response helps us improve Microsoft products and services.',
  },
  csat: {
    title: 'Feedback to Microsoft',
    question: 'How satisfied are you with this Support Assistant experience?*',
    option5: '5 - Very Satisfied',
    option4: '4 - Satisfied',
    option3: '3 - Neutral',
    option2: '2 - Dissatisfied',
    option1: '1 - Very Dissatisfied',
    detailQuestion: 'Please tell us more. Why did you choose that answer?',
    detailPlaceholder:
      'Please do not include any confidential or personal information in your comment',
    privacyNotice:
      'By clicking Send, your feedback will be used to improve Microsoft products and services.',
    privacyLink: 'Privacy statement',
    send: 'Send',
    cancel: 'Cancel',
  },
  reason: {
    dialogLabel: 'Feedback reason',
    title: 'What went wrong?',
    incorrectAnswer: 'Incorrect answer',
    notHelpful: 'Not helpful',
    tooLong: 'Too long',
    tooShort: 'Too short',
    outdated: 'Outdated information',
    offensive: 'Offensive content',
    other: 'Other',
    detailPlaceholder:
      "Add more detail (optional). Please don't include personal information.",
    cancel: 'Cancel',
    submit: 'Send feedback',
  },
  status: {
    signInRequired: 'Sign in is required for this request.',
    liveMessagingUnavailable: 'Live messaging is unavailable.',
    liveChatUnavailable: 'Live chat is unavailable.',
    responseInterrupted: 'Response interrupted before completion.',
    noTrace: 'No trace available yet. Send a message first, then try /duration.',
    startFailed: "We couldn't start the conversation. Please try again.",
    sendFailed: "Your message couldn't be sent. Please try again.",
    liveMessageBlocked: 'Can you please rephrase, I am not able to understand',
    liveMessageScanUnavailable: "Your message couldn't be checked and wasn't sent. Please try again.",
    liveMessageTooLong: 'Please shorten your message to 10,000 characters or fewer and try again.',
    sendStatusUnknown: "Delivery isn't confirmed. Your message may still arrive; don't send it again.",
    genericError: 'Something went wrong. Please try again.',
  },
  live: {
    ended: 'The live agent has ended the conversation.',
    returnedToAssistant: 'You are back with the assistant.',
    unavailable: 'Live chat is unavailable right now.',
    error: 'Live chat is not available at the moment. Please try again later.',
  },
  preChatSurvey: {
    label: 'Pre-chat survey',
    heading: 'Before I connect you',
    caption:
      'Please complete this short pre-chat survey so the live agent has the right context.',
    loading: 'Loading survey…',
    renderFailed: 'Sorry, this pre-chat survey cannot be shown.',
    loadFailed: "Sorry, the pre-chat survey couldn't be loaded. Please try again.",
  },
  postChatSurvey: {
    heading: 'How was your support experience?',
    caption: 'Please take a moment to rate your conversation with the live agent.',
    button: 'Rate your experience',
  },
  tabs: {
    railLabel: 'Chat sections',
    chat: 'Chat',
    myInfo: 'My Info',
    subscriptions: 'Subscriptions',
    history: 'History',
    productProfile: 'Product Profile',
    deviceInformation: 'Device Information',
  },
  panels: {
    notAvailable: '-',
    profileError: "We couldn't load your profile right now.",
    profileLoading: 'Loading your profile...',
    signedInAs: 'Signed in as',
    email: 'Email',
    accountType: 'Account type',
    puid: 'PUID',
    personalAccount: 'Personal Microsoft account',
    workAccount: 'Work or school account',
    manageAccount: 'Manage your Microsoft account (AMC)',
    subscriptionsError: "We couldn't load your subscriptions right now.",
    subscriptionsLoading: 'Loading your subscriptions...',
    subscriptionsEmpty: 'No active subscriptions found.',
    manageSubscriptions: 'Manage subscriptions',
    subscriptionFallbackTitle: 'Subscription',
    subscriptionStateUnknown: 'Unknown',
    trial: 'Trial',
    autoRenewOn: 'Auto-renew on',
    autoRenewOff: 'Auto-renew off',
    renews: 'Renews',
    expires: 'Expires',
    historyEmpty: 'Your recent support cases will appear here.',
    productsError: "We couldn't load your products right now.",
    productsLoading: 'Loading your products...',
    productsEmpty: 'No products found.',
    productsHeading: 'Your products',
    devicesHeading: 'Owned Microsoft devices',
    devicesEmpty: 'Your registered Microsoft devices will appear here.',
    manageDevices: 'Manage your devices',
    operatingSystem: 'Operating system',
    browser: 'Browser',
    language: 'Language',
    screen: 'Screen',
    timezone: 'Timezone',
    userAgent: 'User agent',
    unknown: 'Unknown',
  },
};

/**
 * Layers host overrides on the English defaults, one group at a time, so a host
 * supplying a single key keeps every other string rather than blanking the UI.
 */
export function resolveStrings(override?: CmspChatStringsOverride): CmspChatStrings {
  if (!override) {
    return EN_STRINGS;
  }
  const resolved = {} as CmspChatStrings;
  for (const key of Object.keys(EN_STRINGS) as Array<keyof CmspChatStrings>) {
    Object.assign(resolved, {
      [key]: { ...EN_STRINGS[key], ...(override[key] ?? {}) },
    });
  }
  return resolved;
}

/** Substitutes `{token}` placeholders; unknown tokens are left as-is. */
export function format(
  template: string,
  params: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (match, token: string) =>
    Object.prototype.hasOwnProperty.call(params, token)
      ? String(params[token])
      : match,
  );
}

/** Localize complete server messages only when the host supplied a complete positional template. */
export function formatServerMessage(
  chunk: Pick<CmspChatStreamChunk, 'data' | 'messageKey' | 'messageArgs'>,
  strings: CmspChatStrings,
): string {
  const templates = strings.serverMessages;
  const args = chunk.messageArgs;
  if (!chunk.messageKey || !templates
    || !Object.prototype.hasOwnProperty.call(templates, chunk.messageKey)
    || !Array.isArray(args) || !Array.from(args).every(arg => typeof arg === 'string')) {
    return chunk.data;
  }
  const template = templates[chunk.messageKey];
  if (typeof template !== 'string' || !template.trim()) return chunk.data;

  const used = new Set<number>();
  const remainder = template.replace(/\{(0|[1-9]\d*)\}/g, (_, index: string) => {
    used.add(Number(index));
    return '';
  });
  if (/[{}]/.test(remainder) || [...used].some(index => index >= args.length)
    || !args.every((_, index) => used.has(index))) {
    return chunk.data;
  }
  // Single-pass replacement keeps braces, dollar signs and leading zeros in arguments verbatim.
  return template.replace(/\{(0|[1-9]\d*)\}/g, (_, index: string) => args[Number(index)]);
}

const StringsContext = createContext<CmspChatStrings>(EN_STRINGS);

export const StringsProvider = StringsContext.Provider;

export function useStrings(): CmspChatStrings {
  return useContext(StringsContext);
}
